/**
 * Context for managing Google Analytics (GA4) events.
 *
 * Goals:
 * -[x] Initialize the GA4 client once the user is authenticated
 * -[x] Ensure authentication context is available before logging events
 * -[x] Avoid state updates causing re-renders as this sits high in the component tree
 * -[x] Handle page views manually (otherwise GA4 does try to handle this automatically)
 * -[x] Handle user id and user properties (organisation name, organisation id)
 * -[x] Handle cleaning up the client when the user logs out
 * -[x] Provide a hook to log events
 * -[x] Provide a hook to set user id and user properties (hidden in context and not exposed)
 * -[x] Handle feature flag to enable/disable analytics
 * Non-goals:
 * -[ ] Handle consent tracking
 */
import {
  Analytics,
  initializeAnalytics,
  logEvent,
  setAnalyticsCollectionEnabled,
  setUserId,
  setUserProperties,
} from "firebase/analytics";
import { app } from "integrations/firebase/app";
import {
  createContext,
  PropsWithChildren,
  useCallback,
  use,
  useEffect,
  useMemo,
} from "react";
import { useLocation, useRouter } from "@tanstack/react-router";
import { usesFirebaseEmulator } from "shared/constants/Environment";
import { useAuth } from "contexts/AuthContext";
import { isChaptrEmail } from "utils/chaptrUser";
import { AnalyticsEvent } from "./AnalyticsEvents";

declare global {
  interface Window {
    analytics: Analytics | undefined;
  }
}

export interface IAnalyticsContext {
  gaEvent: (event: AnalyticsEvent) => void;
}

export const AnalyticsContext = createContext<IAnalyticsContext>(
  undefined as never,
);

export const useAnalytics = () => use(AnalyticsContext);

const CHAPTR_ANALYTICS_ORGANISATION_IDS = new Set(
  Object.values({
    demo: "da24c861-33df-42d7-8a68-2edcd47638ad",
    test: "fdde346a-39a4-4964-ba82-59d5202bd10c",
  }),
);

const getAnalytics = () => window.analytics;

const initialiseAnalytics = () => {
  const client = initializeAnalytics(app, {
    config: {
      send_page_view: false, // handle page views manually
    },
  });

  setAnalyticsCollectionEnabled(client, false);

  return client;
};

const resetAnalytics = () => {
  const analytics = getAnalytics();

  if (!analytics) {
    return;
  }

  setUserId(analytics, null);
  setUserProperties(analytics, {
    organisation_id: null,
    organisation_name: null,
    group: null,
  });
};

export const AnalyticsContextProvider = ({ children }: PropsWithChildren) => {
  const location = useLocation();
  const router = useRouter();
  const { loading, session } = useAuth();
  const user = session?.user;
  const organisation = session?.organisation;
  const isChaptrUser = isChaptrEmail(user?.email);
  // Test and Demo retain internal analytics because Chaptr activity is their only product usage.
  const analyticsCollectionAllowed =
    Boolean(user && organisation) &&
    (!isChaptrUser ||
      CHAPTR_ANALYTICS_ORGANISATION_IDS.has(organisation?.id ?? ""));

  useEffect(() => {
    if (loading) {
      return;
    }

    if (usesFirebaseEmulator) {
      return;
    }

    // biome-ignore lint/suspicious/noAssignInExpressions: creates window.analytics once and reuses it on later runs
    const analytics = (window.analytics ??= initialiseAnalytics());

    if (!analyticsCollectionAllowed || !user) {
      setAnalyticsCollectionEnabled(analytics, false);
      resetAnalytics();
    } else {
      setUserId(analytics, user.id);
      setUserProperties(analytics, {
        group: user.settings?.analyticsGroup || "default",
        organisation_id: organisation?.id,
        organisation_name: organisation?.name,
      });
    }
  }, [
    analyticsCollectionAllowed,
    loading,
    location.pathname,
    user,
    organisation,
  ]);

  const gaEvent = useCallback(
    (event: AnalyticsEvent) => {
      const analytics = getAnalytics();

      if (
        !analytics ||
        !analyticsCollectionAllowed ||
        !user ||
        usesFirebaseEmulator
      ) {
        return;
      }

      const payload = {
        ...event.payload,
        organisation_id: organisation?.id,
        organisation_name: organisation?.name,
        organisation: organisation?.name,
        user_id: user.id,
      };

      logEvent(analytics, event.type as string, payload); // Use `as string` to help TS select the right overloading fn
    },
    [analyticsCollectionAllowed, user, organisation],
  );

  useEffect(() => {
    const analytics = getAnalytics();

    if (!analytics || !analyticsCollectionAllowed) {
      return;
    }

    if (usesFirebaseEmulator) {
      return;
    }

    // Re-enable analytics collection, once the client is initialised with a user id
    setAnalyticsCollectionEnabled(analytics, true);

    // Log the initial page view, that would have been missed due to disabled analytics collection
    const { pathname, searchStr } = router.state.location;

    gaEvent({
      type: "page_view",
      payload: {
        page_path: pathname,
        page_location: window.location.href,
        page_title: document.title,
        search_params: searchStr,
      },
    });
  }, [analyticsCollectionAllowed, gaEvent, router]);

  // The location changes when navigation starts; the new page's title is only
  // in the document once the route has rendered.
  useEffect(
    () =>
      router.subscribe("onRendered", ({ toLocation, hrefChanged }) => {
        if (!hrefChanged) {
          return;
        }

        gaEvent({
          type: "page_view",
          payload: {
            page_path: toLocation.pathname,
            page_location: window.location.href,
            page_title: document.title,
            search_params: toLocation.searchStr,
          },
        });
      }),
    [gaEvent, router],
  );

  const value = useMemo(() => {
    return { gaEvent };
  }, [gaEvent]);

  return <AnalyticsContext value={value}>{children}</AnalyticsContext>;
};
